// imports
import { createSlice } from '@reduxjs/toolkit';

type BannerMessage = {
  id: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
};

type LegendContent = {
  id: string;
  name: string;
  units: string;
  colors?: string[];
  binLabels?: string[];
  month?: string;
  year?: string;
  variableId?: string;
  variableLink?: string;
};

// selected AWD feature related types
type selectedAwdFeatureType = {
  longitude: number;
  latitude: number;
  name: string;
  HUC12: string;
  value: string;
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    infoDialogOpen: false,
    shareDialogOpen: false,
    bannerMessages: [] as BannerMessage[],
    legendContent: [] as LegendContent[],
    mapDownloadDialogOpen: false,
    dataDownloadDialogOpen: false,
    selectedAwdFeature: null as selectedAwdFeatureType | null,
    awdNoData: false,
    awdNoDataProbing: true,
    isFullExtent: false,
    rulerActive: false,
    alertDismissed: false,
  },
  reducers: {
    toggleInfoDialog: (state) => {
      state.infoDialogOpen = !state.infoDialogOpen;
    },
    setInfoDialogOpen: (state, action) => {
      state.infoDialogOpen = action.payload;
    },
    toggleShareDialog: (state) => {
      state.shareDialogOpen = !state.shareDialogOpen;
    },
    addBannerMessage: (state, action) => {
      // if id already exists, do not add
      if (state.bannerMessages.find((msg) => msg.id === action.payload.id)) {
        return;
      }
      state.bannerMessages.push(action.payload);
    },
    removeBannerMessage: (state, action) => {
      state.bannerMessages = state.bannerMessages.filter(
        (msg) => msg.id !== action.payload
      );
    },
    addLegendContent: (state, action) => {
      // if id already exists, do not add
      if (state.legendContent.find((msg) => msg.id === action.payload.id)) {
        return;
      }
      state.legendContent.push(action.payload);
    },
    removeLegendContent: (state, action) => {
      state.legendContent = state.legendContent.filter(
        (msg) => msg.id !== action.payload
      );
    },
    toggleMapDownloadDialog: (state) => {
      state.mapDownloadDialogOpen = !state.mapDownloadDialogOpen;
    },
    toggleDataDownloadDialog: (state) => {
      state.dataDownloadDialogOpen = !state.dataDownloadDialogOpen;
    },
    setSelectedAwdFeature: (state, action) => {
      state.selectedAwdFeature = action.payload;
    },
    setAwdNoData: (state, action) => {
      state.awdNoData = action.payload;
    },
    setAwdNoDataProbing: (state, action) => {
      state.awdNoDataProbing = action.payload;
    },
    toggleFullExtent: (state) => {
      state.isFullExtent = !state.isFullExtent;
    },
    toggleRuler: (state) => {
      state.rulerActive = !state.rulerActive;
    },
    dismissAlert: (state) => {
      state.alertDismissed = true;
    },
  },
});

export const {
  toggleInfoDialog,
  setInfoDialogOpen,
  toggleShareDialog,
  addBannerMessage,
  removeBannerMessage,
  addLegendContent,
  removeLegendContent,
  toggleMapDownloadDialog,
  toggleDataDownloadDialog,
  setSelectedAwdFeature,
  setAwdNoData,
  setAwdNoDataProbing,
  toggleFullExtent,
  toggleRuler,
  dismissAlert,
} = uiSlice.actions;
export default uiSlice.reducer;
